import { Fragment, useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { MdInfoOutline } from 'react-icons/md'
import { useDroppable, useDraggable, useDndContext } from '@dnd-kit/core'
import useTranslation from '../../hooks/useTranslation'
import { computeUnavailableZones } from '../../hooks/useWorkPlanner'
import Loader from '../generic/Loader'
import WorkPlannerOpInfoModal from './WorkPlannerOpInfoModal'

export const SLOT_HEIGHT_V  = 10    // px per 5-minute slot
export const COL_WIDTH_V    = 160   // px per work-center column
const TIME_AXIS_WIDTH        = 56   // px for the sticky time-label left column
const WC_HEADER_HEIGHT       = 52   // px for the sticky WC-name top row

const addMinutes = (date, m) => new Date(date.getTime() + m * 60000)

// ── Styled components ──────────────────────────────────────────────────────

const BoardWrapper = styled.div`
    flex: 1;
    overflow: auto;
    min-height: 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.area.highBg} transparent;
    &::-webkit-scrollbar { width: 6px; height: 6px; }
    &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.area.highBg}; border-radius: 3px; }
`

// CSS Grid: first column = time axis, remaining = one per work center.
// First row = WC header (explicit height); all subsequent rows = slot height (auto).
const VerticalGrid = styled.div`
    display: grid;
    grid-template-columns: ${TIME_AXIS_WIDTH}px repeat(${({ $colCount }) => $colCount}, ${COL_WIDTH_V}px);
    grid-template-rows: ${WC_HEADER_HEIGHT}px;
    grid-auto-rows: ${SLOT_HEIGHT_V}px;
`

// Top-left corner — sticky on both axes
const CornerCell = styled.div`
    grid-column: 1;
    grid-row: 1;
    position: sticky;
    top: 0;
    left: 0;
    z-index: 30;
    background: ${({ theme }) => theme.card.background};
    border-right: 1px solid ${({ theme }) => theme.area.lightBorder};
    border-bottom: 2px solid ${({ theme }) => theme.area.lightBorder};
    display: flex;
    align-items: center;
    padding: 0 0.5rem;
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.label};
`

// Work-center name cells — sticky to top
const WcHeaderCell = styled.div`
    grid-row: 1;
    position: sticky;
    top: 0;
    z-index: 20;
    background: ${({ theme }) => theme.card.background};
    border-right: 1px solid ${({ theme }) => theme.area.lightBorder};
    border-bottom: 2px solid ${({ theme }) => theme.area.lightBorder};
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 0.75rem;
    gap: 2px;
    overflow: hidden;
`

const WcNameV = styled.span`
    font-size: var(--font-S);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const WcMetaV = styled.span`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

// Time labels — sticky to left edge
const TimeLabelCellV = styled.div`
    grid-column: 1;
    position: sticky;
    left: 0;
    z-index: ${({ $isHour, $isHalf }) => $isHour ? 12 : $isHalf ? 11 : 10};
    background: ${({ theme }) => theme.card.background};
    border-right: 1px solid ${({ theme }) => theme.area.lightBorder};
    border-top: 1px solid ${({ $isHour, $isFirst, theme }) =>
        ($isHour && !$isFirst) ? theme.area.lightBorder : 'transparent'
    };
    display: flex;
    align-items: flex-start;
    padding-top: 1px;
    padding-left: 6px;
    font-size: ${({ $isHour, $isHalf }) => $isHour ? '11px' : $isHalf ? '10px' : '9px'};
    font-weight: ${({ $isHour }) => $isHour ? '600' : '400'};
    color: ${({ $isHour, theme }) => $isHour ? theme.font.color : theme.font.label};
    overflow: visible;
    white-space: nowrap;
    pointer-events: none;
`

// Droppable slot cells
const SlotCellV = styled.div`
    border-top: 1px solid ${({ $isHour, $isFirst, theme }) =>
        ($isHour && !$isFirst) ? theme.area.lightBorder : 'transparent'
    };
    border-right: 1px solid ${({ theme }) => theme.area.lightBorder};
    background: ${({ $isOver, $constrained, $outsideShift, $greyed, theme }) =>
        $isOver        ? `${theme.btn.primary}22` :
        $constrained   ? `${theme.btn.primary}18` :
        $outsideShift  ? `${theme.btn.danger}28` :
        $greyed        ? theme.area.highBg :
        'transparent'
    };
    cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
    transition: background 0.1s;
`

const UnavailableZoneV = styled.div`
    position: absolute;
    left: 0; right: 0;
    top: ${({ $top }) => $top}px;
    height: ${({ $height }) => $height}px;
    background: ${({ theme }) => `${theme.btn.danger}22`};
    border-top: 2px solid ${({ theme }) => `${theme.btn.danger}60`};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-around;
    overflow: hidden;
    pointer-events: none;
    z-index: 2;
`

const UnavailableLabelV = styled.span`
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.btn.danger};
    opacity: 0.55;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    user-select: none;
`

// Per-column overlay spanning all slot rows — blocks are absolute-positioned inside
const ColumnOverlay = styled.div`
    grid-row: ${({ $startRow, $endRow }) => `${$startRow} / ${$endRow}`};
    position: relative;
    pointer-events: none;
    z-index: 5;
`

const BlockV = styled.div`
    position: absolute;
    top: calc(${({ $top }) => $top}px + 2px);
    height: calc(${({ $height }) => $height}px - 4px);
    left: 3px;
    right: 3px;
    min-height: 16px;
    background: ${({ theme, $moving, $foreign }) =>
        $moving  ? `${theme.btn.primary}18` :
        $foreign ? theme.area.highBg :
        theme.card.background
    };
    border-radius: 0.4rem;
    border: 1.5px solid ${({ theme, $moving, $foreign }) =>
        $moving  ? theme.btn.primary :
        $foreign ? theme.area.border :
        theme.area.lightBorder
    };
    border-top: 3px solid ${({ theme, $moving, $foreign }) =>
        $moving  ? theme.btn.primary :
        $foreign ? theme.area.border :
        theme.area.lightBorder
    };
    box-shadow: ${({ theme, $moving }) => $moving
        ? `0 0 0 3px ${theme.btn.primary}33, 0 2px 8px rgba(0,0,0,0.12)`
        : '0 1px 3px rgba(0,0,0,0.07)'
    };
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 0.25rem 0.4rem;
    overflow: hidden;
    pointer-events: ${({ $foreign }) => $foreign ? 'none' : 'auto'};
    cursor: ${({ $foreign }) => $foreign ? 'default' : 'grab'};
    opacity: ${({ $dragging, $foreign }) => $dragging ? 0.35 : $foreign ? 0.45 : 1};
    transition: box-shadow 0.15s, border-color 0.15s, background 0.15s, opacity 0.1s;
    &:active { cursor: ${({ $foreign }) => $foreign ? 'default' : 'grabbing'}; }
`

const BlockNameV = styled.span`
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`

const BlockSubV = styled.span`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
    margin-top: 1px;
`

const InfoBtnV = styled.button`
    position: absolute;
    bottom: 3px;
    right: 4px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    line-height: 1;
    color: ${({ theme }) => theme.font.label};
    opacity: 0.6;
    transition: opacity 0.15s, color 0.15s;
    pointer-events: auto;

    &:hover {
        opacity: 1;
        color: ${({ theme }) => theme.btn.primary};
    }
`

// Horizontal line marking the earliest-start constraint
const ConstraintLineV = styled.div`
    position: absolute;
    top: ${({ $top }) => $top}px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.btn.danger};
    z-index: 2;
    pointer-events: none;

    &::before {
        content: '';
        position: absolute;
        top: -4px;
        left: 0;
        width: 10px;
        height: 10px;
        background: ${({ theme }) => theme.btn.danger};
        border-radius: 50%;
    }
`

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 3rem;
    font-size: var(--font-M);
    color: ${({ theme }) => theme.font.label};
`

// ── Helpers ────────────────────────────────────────────────────────────────

const formatTimeLabelV = (slot) => {
    const h = slot.getHours().toString().padStart(2, '0')
    const m = slot.getMinutes()
    if (m === 0)  return `${h}:00`
    if (m === 30) return ':30'
    return ''
}

const slotToTop = (slotTime, shiftStart, slotMinutes) => {
    const diffMs = slotTime.getTime() - shiftStart.getTime()
    return (diffMs / (slotMinutes * 60000)) * SLOT_HEIGHT_V
}

const durationToHeight = (startMs, endMs, slotMinutes) => {
    const diffMs = endMs - startMs
    return (diffMs / (slotMinutes * 60000)) * SLOT_HEIGHT_V
}

// ── Droppable cell ─────────────────────────────────────────────────────────

const DroppableCellV = ({ id, data, disabled, greyed, constrained, outsideShift, isHour, isFirst, isSnapped, style }) => {
    const { isOver, setNodeRef } = useDroppable({ id, data, disabled })
    const highlighted = isSnapped != null ? isSnapped : (isOver && !disabled)
    return (
        <SlotCellV
            ref={setNodeRef}
            style={style}
            $isOver={highlighted}
            $disabled={disabled}
            $greyed={greyed}
            $constrained={constrained}
            $outsideShift={outsideShift}
            $isHour={isHour}
            $isFirst={isFirst}
        />
    )
}

// ── Draggable block ────────────────────────────────────────────────────────

const DraggableBlockV = ({ booking, top, height, isMoving, onSelect, onInfo, children }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `board::${booking.id}`,
        data: { op: booking }
    })
    return (
        <BlockV
            ref={setNodeRef}
            $top={top}
            $height={height}
            $moving={isMoving}
            $dragging={isDragging}
            onClick={() => onSelect?.(booking.id)}
            {...listeners}
            {...attributes}
        >
            {children}
            {onInfo && (
                <InfoBtnV
                    onClick={(e) => { e.stopPropagation(); onInfo(booking) }}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Details"
                >
                    <MdInfoOutline />
                </InfoBtnV>
            )}
        </BlockV>
    )
}

// ── Main component ─────────────────────────────────────────────────────────

const WorkPlannerBoardVertical = ({
    selectedOp, allResources, eligibleResourceIds, currentOpIds, onSelectOp,
    bookings, earliestStart, shiftStart, shiftHours, bufferHours, slotMinutes,
    snapPreview, loading, opInfoModalConfig, resourceEffectiveShifts, fieldConfig, labelConfig
}) => {
    const { t } = useTranslation()
    const [infoOp, setInfoOp] = useState(null)
    const handleInfo      = useCallback((op) => setInfoOp(op), [])
    const handleInfoClose = useCallback(() => setInfoOp(null), [])

    const { active: dndActive, over: dndOver } = useDndContext()
    const isDragActive = !!dndActive
    const snapPreviewMs   = snapPreview ? snapPreview.getTime() : null
    const snapPreviewWcId = dndOver?.data.current?.resource?.id ?? null

    const shiftEnd = addMinutes(shiftStart, shiftHours * 60)

    const boardDate = useMemo(() => {
        const d = new Date(shiftStart)
        d.setHours(0, 0, 0, 0)
        return d
    }, [shiftStart])

    const slots = useMemo(() => {
        const totalDisplayHours = shiftHours + (bufferHours ?? 0)
        const count = (totalDisplayHours * 60) / slotMinutes
        return Array.from({ length: count }, (_, i) => addMinutes(shiftStart, i * slotMinutes))
    }, [shiftStart, shiftHours, bufferHours, slotMinutes])
    const slotCount = slots.length

    const seqField   = fieldConfig?.sequence         ?? 'sequence'
    const labelField = fieldConfig?.taskLabel        ?? 'name'
    const subField   = fieldConfig?.taskSubLabel
    const startField = fieldConfig?.startTime        ?? 'plannedStart'
    const endField   = fieldConfig?.endTime          ?? 'plannedEnd'
    const resLabelField = fieldConfig?.resourceLabel    ?? 'name'
    const resSubField   = fieldConfig?.resourceSubLabel ?? 'code'

    const wcZones = useMemo(() => {
        const isoDay = boardDate.getDay() === 0 ? 7 : boardDate.getDay()
        const map = {}
        allResources.forEach(r => {
            const effectiveShift = resourceEffectiveShifts?.[r.id] ?? null
            const activeDays = effectiveShift?.activeDays ?? []
            const dayIsActive = !activeDays.length || activeDays.includes(isoDay)
            if (!dayIsActive && slots.length > 0) {
                map[r.id] = [{ start: slots[0], end: addMinutes(slots[slots.length - 1], slotMinutes) }]
            } else {
                map[r.id] = computeUnavailableZones(
                    slots, effectiveShift?.shiftSlots ?? null, boardDate, slotMinutes
                )
            }
        })
        return map
    }, [allResources, resourceEffectiveShifts, boardDate, slotMinutes, slots])

    if (loading && allResources.length === 0) {
        return <BoardWrapper><Loader /></BoardWrapper>
    }

    if (allResources.length === 0 && !loading) {
        return <BoardWrapper><EmptyState>{t(labelConfig?.noResources ?? 'manufacturing.planner.board.noWorkCenters')}</EmptyState></BoardWrapper>
    }

    // Row 1 = WC header; slot rows start at row 2
    const firstSlotRow = 2
    const lastRow = firstSlotRow + slotCount  // exclusive end for grid-row span

    return (
        <BoardWrapper>
            <VerticalGrid $colCount={allResources.length}>

                {/* ── Corner ── */}
                <CornerCell>{t(labelConfig?.resourceColumn ?? 'manufacturing.planner.board.workCenter')}</CornerCell>

                {/* ── Resource header row ── */}
                {allResources.map((r, i) => (
                    <WcHeaderCell key={r.id} style={{ gridColumn: i + 2 }}>
                        <WcNameV>{r[resLabelField]}</WcNameV>
                        {r[resSubField] && <WcMetaV>{r[resSubField]}</WcMetaV>}
                    </WcHeaderCell>
                ))}

                {/* ── Time labels + droppable cells, one row per slot ── */}
                {slots.map((slot, j) => {
                    const rowIndex  = firstSlotRow + j
                    const isHour  = slot.getMinutes() === 0
                    const isHalf  = slot.getMinutes() === 30
                    const isFirst = j === 0

                    return (
                        <Fragment key={slot.getTime()}>
                            <TimeLabelCellV $isHour={isHour} $isHalf={isHalf} $isFirst={isFirst} style={{ gridRow: rowIndex }}>
                                {formatTimeLabelV(slot)}
                            </TimeLabelCellV>

                            {allResources.map((r, i) => {
                                const isEligible     = eligibleResourceIds.has(r.id)
                                const isPastShift    = shiftEnd != null && slot >= shiftEnd
                                const isConstrained  = isEligible && earliestStart != null && slot < earliestStart
                                const hasShift       = !!(resourceEffectiveShifts?.[r.id])
                                const isOutsideShift = (wcZones[r.id] ?? []).some(z => slot >= z.start && slot < z.end)

                                const isDisabled = !isEligible || isConstrained ||
                                    (!selectedOp && !isDragActive)
                                const isGreyed   = !hasShift && isPastShift

                                const isThisWcActive = snapPreviewWcId === r.id
                                const isSnapped = (isThisWcActive && snapPreviewMs != null)
                                    ? slot.getTime() === snapPreviewMs
                                    : null

                                return (
                                    <DroppableCellV
                                        key={r.id}
                                        id={`${r.id}::${slot.getTime()}`}
                                        data={{ resource: r, slotTime: slot }}
                                        disabled={isDisabled}
                                        greyed={isGreyed}
                                        constrained={isConstrained}
                                        outsideShift={isOutsideShift}
                                        isHour={isHour}
                                        isFirst={isFirst}
                                        isSnapped={isSnapped}
                                        style={{ gridColumn: i + 2, gridRow: rowIndex }}
                                    />
                                )
                            })}
                        </Fragment>
                    )
                })}

                {/* ── Block overlays — one per resource column, spans all slot rows ── */}
                {allResources.map((r, i) => {
                    const isEligible  = eligibleResourceIds.has(r.id)
                    const wcBookings  = bookings[r.id] ?? []
                    const constraintTop = earliestStart && isEligible
                        ? slotToTop(earliestStart, shiftStart, slotMinutes)
                        : null
                    const unavailableZones = wcZones[r.id] ?? []

                    return (
                        <ColumnOverlay
                            key={`overlay-${r.id}`}
                            style={{ gridColumn: i + 2 }}
                            $startRow={firstSlotRow}
                            $endRow={lastRow}
                        >
                            {constraintTop != null && constraintTop >= 0 && (
                                <ConstraintLineV $top={constraintTop} />
                            )}

                            {unavailableZones.map((zone, zi) => {
                                const top    = slotToTop(zone.start, shiftStart, slotMinutes)
                                const height = durationToHeight(zone.start.getTime(), zone.end.getTime(), slotMinutes)
                                const labelCount = height >= 30 ? Math.max(1, Math.floor(height / 60)) : 0
                                return (
                                    <UnavailableZoneV key={zi} $top={top} $height={height}>
                                        {Array.from({ length: labelCount }, (_, li) => (
                                            <UnavailableLabelV key={li}>{` ${t(labelConfig?.unavailable ?? 'all.unavailable')} `}</UnavailableLabelV>
                                        ))}
                                    </UnavailableZoneV>
                                )
                            })}

                            {wcBookings.map(booking => {
                                if (!booking[startField] || !booking[endField]) return null
                                const bStart = new Date(booking[startField])
                                const bEnd   = new Date(booking[endField])
                                const visStart = bStart < shiftStart ? shiftStart : bStart
                                const visEnd   = bEnd
                                if (visStart >= visEnd) return null

                                const top    = slotToTop(visStart, shiftStart, slotMinutes)
                                const height = durationToHeight(visStart.getTime(), visEnd.getTime(), slotMinutes)
                                if (height <= 0) return null

                                const isMoving  = selectedOp != null && booking.id === selectedOp.id
                                const isForeign = currentOpIds != null && !currentOpIds.has(booking.id)

                                const blockContent = (
                                    <>
                                        <BlockNameV>
                                            {booking[seqField] != null ? `${booking[seqField]}. ` : ''}{booking[labelField]}
                                        </BlockNameV>
                                        {subField && booking[subField] && (
                                            <BlockSubV>{booking[subField]}</BlockSubV>
                                        )}
                                    </>
                                )

                                if (isForeign) {
                                    return (
                                        <BlockV key={booking.id} $top={top} $height={height} $foreign>
                                            {blockContent}
                                        </BlockV>
                                    )
                                }

                                return (
                                    <DraggableBlockV
                                        key={booking.id}
                                        booking={booking}
                                        top={top}
                                        height={height}
                                        isMoving={isMoving}
                                        onSelect={onSelectOp}
                                        onInfo={handleInfo}
                                    >
                                        {blockContent}
                                    </DraggableBlockV>
                                )
                            })}
                        </ColumnOverlay>
                    )
                })}

            </VerticalGrid>

            {infoOp && <WorkPlannerOpInfoModal op={infoOp} config={opInfoModalConfig} onClose={handleInfoClose} />}
        </BoardWrapper>
    )
}

export default WorkPlannerBoardVertical
